import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AppBar, Tag, Banner, Btn, CandidatePicker, cautionVariant } from "../components/index.js";
import { Card } from "../components/Card.js";
import { LabelingBlock, type LabelValue } from "../components/LabelingBlock.js";
import { LoadingState, ErrorState } from "../components/EmptyState.js";
import { useSurface } from "../lib/useSurface.js";
import { useAsync } from "../lib/useAsync.js";
import { manuals, stores as storesApi, hasRole, fetchMediaBlob, mediaUrl } from "../lib/api.js";
import type { Store } from "@tebiki/shared";
import { getSourceVideo, removeSourceVideo } from "../lib/offline.js";
import { recallCaptureOrigin, forgetCaptureOrigin } from "../lib/captureOrigin.js";
import { loadVideo, extractFrame, disposeVideo } from "../lib/frames.js";
import { mmss, type StepWithCandidates } from "../lib/demo.js";
import { bboxToFrame } from "../lib/bbox.js";
import { Icon } from "../components/Icon.js";
import type { BBox } from "../components/Frame.js";

function toBBox(step: StepWithCandidates): BBox | null {
  return bboxToFrame(step.bbox, step.objectLabel);
}

/** Quality note for synthesized candidates (no real frame). */
const SYNTH_LABELS = ["くっきり", "別の瞬間", "やや早い"];

/**
 * A candidate the picker can display from a **stable R2 image URL** (no blob dependency).
 * `mediaId` is set once the frame is materialized to R2 (either it arrived already-stored in
 * `step.keyframeCandidates`, or we uploaded a freshly extracted frame). When `mediaId` is set,
 * the chosen candidate is adopted via `chooseKeyframe` on confirm; otherwise we fall back to
 * time-only selection (demo / image flow / failed materialization).
 */
interface R2Candidate {
  timecodeMs: number;
  /** absolute, auth-tokenized R2 image URL (mediaUrl-resolved), or null when not yet materialized */
  imageUrl: string | null;
  mediaId?: string;
  label?: string;
  recommended?: boolean;
  bbox?: BBox | null;
}

/**
 * Candidate frame *times* (ms, best-first) for a step. Prefers the real contract
 * (`keyframeCandidatesMs`), falls back to the demo augmentation (`candidates`), then
 * synthesizes three around the representative time so the 3-up layout always renders.
 */
function candidateTimesFor(step: StepWithCandidates): number[] {
  if (step.keyframeCandidatesMs?.length) return step.keyframeCandidatesMs;
  if (step.candidates?.length) return step.candidates.map((c) => c.timecodeMs);
  const base = step.videoTimecodeMs ?? 0;
  return [base, base + 3000, Math.max(0, base - 3000)];
}

/**
 * Already-materialized R2 candidates for a step, or null when none are stored yet.
 * When present the picker shows these stable `<img>` URLs directly and never re-extracts.
 */
function materializedCandidates(step: StepWithCandidates): R2Candidate[] | null {
  if (!step.keyframeCandidates?.length) return null;
  return step.keyframeCandidates.map((c, i) => ({
    timecodeMs: c.timecodeMs,
    imageUrl: mediaUrl(c.imageUrl),
    // mediaId makes confirm ADOPT the chosen frame (chooseKeyframe). Older payloads lack the
    // field — recover it from the /api/media/<id> URL so a revisit never silently degrades to
    // time-only selection (the "picked a frame but nothing changed" bug).
    mediaId: c.mediaId ?? /\/api\/media\/([^/?#]+)/.exec(c.imageUrl)?.[1],
    label: step.candidates?.[i]?.label ?? SYNTH_LABELS[i],
    recommended: i === 0,
    // The detected target box is drawn on EVERY candidate: frames are seconds apart in the same
    // scene, and confirm re-runs detection on the adopted frame anyway.
    bbox: toBBox(step),
  }));
}

/** Retry an async op with linear backoff — a network blip must not cost a frame. */
async function withRetry<T>(fn: () => Promise<T>, tries: number, baseDelayMs: number): Promise<T> {
  let lastErr: unknown;
  for (let a = 0; a < tries; a++) {
    try {
      return await fn();
    } catch (e) {
      lastErr = e;
      if (a < tries - 1) await new Promise((r) => setTimeout(r, baseDelayMs * (a + 1)));
    }
  }
  throw lastErr;
}

/** Build placeholder/synthesized candidate cells (no decoded pixels, no R2 image). */
function synthCandidates(step: StepWithCandidates, times: number[]): R2Candidate[] {
  return times.map((timecodeMs, i) => ({
    timecodeMs,
    imageUrl: null,
    label: step.candidates?.[i]?.label ?? SYNTH_LABELS[i] ?? "別の瞬間",
    recommended: i === 0,
    bbox: toBBox(step),
  }));
}

interface ExtractState {
  /** materialized R2 candidates keyed by stepId, parallel to candidateTimesFor(step) */
  candidatesByStep: Record<string, R2Candidate[]>;
  done: number;
  total: number;
  /** idle: nothing to extract (all already on R2, or demo). materializing: extracting+uploading. */
  status: "idle" | "materializing" | "ready" | "error";
  error?: string;
}

/**
 * Screen G — multi-candidate keyframe picker. THE product differentiator: the backend ships
 * candidate frame *times* per step (★ = first/recommended) and the client decodes the real
 * pixels from the source video (cached recording, else the Range-served originalVideoUrl) so
 * the user picks the best true frame with one tap. Confirming uploads the chosen JPEGs and
 * advances to preview. Image-flow manuals keep the original synthesized behaviour.
 */
export function Picker() {
  useSurface("field");
  const navigate = useNavigate();
  const { id = "" } = useParams();

  const { data, loading, error, offline, reload } = useAsync(() => manuals.get(id), [id]);

  const steps = (data?.steps ?? []) as StepWithCandidates[];
  const isVideo = data?.sourceType === "video";
  const [selections, setSelections] = useState<Record<string, number>>({});
  const [confirming, setConfirming] = useState(false);

  // ---- AI生成後のやり直し: 生成結果を破棄して終える / 撮影からやり直す ----
  const canManage = hasRole("owner", "admin", "editor");
  const [removing, setRemoving] = useState<null | "delete" | "retake">(null);
  const [removeError, setRemoveError] = useState<string | null>(null);

  /** Cascade-delete the generated manual + tidy the local recording cache / origin record. */
  async function destroyManual(): Promise<boolean> {
    try {
      await manuals.delete(id);
    } catch {
      setRemoveError("削除できませんでした。通信環境を確認してもう一度お試しください。");
      return false;
    }
    void removeSourceVideo(id);
    forgetCaptureOrigin(id);
    return true;
  }

  async function deleteGenerated() {
    if (removing) return;
    if (!window.confirm("生成されたマニュアルを削除しますか？この操作は取り消せません。")) return;
    setRemoveError(null);
    setRemoving("delete");
    if (!(await destroyManual())) {
      setRemoving(null);
      return;
    }
    navigate("/dashboard", { replace: true });
  }

  async function retakeCapture() {
    if (removing) return;
    if (!window.confirm("この生成結果を削除して、撮影し直しますか？")) return;
    setRemoveError(null);
    setRemoving("retake");
    // Return to the SAME entry point (screen recording vs camera); fall back on sourceType.
    const origin = recallCaptureOrigin(id) ?? (data?.sourceType === "image" ? "image" : "video");
    if (!(await destroyManual())) {
      setRemoving(null);
      return;
    }
    navigate(`/capture/${origin}`, { replace: true });
  }

  // ---- creation-time labeling (store × category × tags) ----
  const canLabel = hasRole("owner", "admin", "editor");
  const [labels, setLabels] = useState<LabelValue | null>(null);
  const [storeList, setStoreList] = useState<Store[]>([]);
  const [catSuggest, setCatSuggest] = useState<string[]>([]);
  // Seed the label form once the manual loads (AI may have proposed a category).
  useEffect(() => {
    if (data && labels === null) {
      setLabels({
        storeId: data.meta.storeId ?? null,
        category: data.meta.category ?? "",
        tags: data.meta.tags ?? [],
      });
    }
  }, [data, labels]);
  // Load store options + existing category suggestions (best-effort).
  useEffect(() => {
    if (!canLabel) return;
    void storesApi.list().then(setStoreList).catch(() => setStoreList([]));
    void manuals
      .list()
      .then((r) => setCatSuggest([...new Set(r.items.map((m) => m.meta?.category).filter((c): c is string => !!c))]))
      .catch(() => setCatSuggest([]));
  }, [canLabel]);

  // Candidate *times* per step (stable for a given manual payload).
  const timesByStep = useMemo(
    () => Object.fromEntries(steps.map((s) => [s.stepId, candidateTimesFor(s)] as const)),
    [steps],
  );

  // Steps that already have R2-materialized candidates — shown directly, never re-extracted.
  const materializedByStep = useMemo(
    () => Object.fromEntries(steps.map((s) => [s.stepId, materializedCandidates(s)] as const)),
    [steps],
  );

  // ---- one-time client-side extraction + R2 upload (only for steps with no stored candidates) ----
  const [extract, setExtract] = useState<ExtractState>({
    candidatesByStep: {},
    done: 0,
    total: 0,
    status: "idle",
  });
  // Bumping the nonce re-runs the whole extraction pass (再試行). One automatic retry fires
  // after a fully-failed pass; the banner button allows unlimited manual retries.
  const [extractNonce, setExtractNonce] = useState(0);
  const autoRetriedRef = useRef(false);

  // Stable key of which steps still need materializing — drives the effect without re-running on
  // every render. Only steps that arrived WITHOUT keyframeCandidates need extraction.
  const pendingKey = useMemo(
    () =>
      steps
        .filter((s) => !s.keyframeCandidates?.length)
        .map((s) => s.stepId)
        .join(","),
    [steps],
  );

  useEffect(() => {
    if (loading || !isVideo || !steps.length) return;
    // Steps still needing materialization (no stored R2 candidates).
    const pending = steps.filter((s) => !s.keyframeCandidates?.length);
    if (!pending.length) {
      // Everything is already on R2 — display directly, no extraction.
      setExtract({ candidatesByStep: {}, done: 0, total: 0, status: "idle" });
      return;
    }

    let cancelled = false;
    let video: HTMLVideoElement | null = null;

    const total = pending.reduce((n, s) => n + (timesByStep[s.stepId]?.length ?? 0), 0);
    setExtract({ candidatesByStep: {}, done: 0, total, status: "materializing" });

    (async () => {
      // Source: prefer the locally cached recording, else the Range-served original video URL
      // DIRECTLY — the browser then fetches only the moov + seeked segments instead of the whole
      // file (a 139MB recording used to hang here downloading everything before the first frame).
      // The media route sends CORS headers and <video crossOrigin="anonymous"> keeps the canvas
      // untainted; environments that still can't play the direct URL fall back to the full blob.
      let src: string | null = null;
      let objectUrl: string | null = null;
      const cached = await getSourceVideo(id).catch(() => null);
      if (cached) {
        objectUrl = URL.createObjectURL(cached);
        src = objectUrl;
      } else if (data?.media.originalVideoUrl) {
        src = mediaUrl(data.media.originalVideoUrl);
      }
      if (!src) {
        // No decodable source (e.g. demo manual): honest error state — the banner explains the
        // time-based fallback still works (候補の時刻でそのまま確定できます).
        if (!cancelled) setExtract((s) => ({ ...s, status: "error", error: "動画を取得できませんでした" }));
        return;
      }

      try {
        try {
          video = await loadVideo(src);
        } catch (e) {
          // Direct Range-served URL unplayable (e.g. strict iOS taint quirks): download the whole
          // blob via authenticated fetch as the legacy fallback.
          if (!objectUrl && data?.media.originalVideoUrl) {
            const blob = await fetchMediaBlob(data.media.originalVideoUrl);
            if (cancelled) return;
            objectUrl = URL.createObjectURL(blob);
            src = objectUrl;
            video = await loadVideo(src);
          } else {
            throw e;
          }
        }
        if (cancelled) return;
        const vid = video;
        let done = 0;
        let materialized = 0;
        for (const step of pending) {
          if (cancelled) break;
          const times = timesByStep[step.stepId] ?? [];
          const cands: R2Candidate[] = [];
          for (let i = 0; i < times.length; i++) {
            if (cancelled) break;
            const timecodeMs = times[i];
            try {
              // Extract → upload to R2 → display from the returned stable image URL. Both ops
              // retry (seek hiccups / network blips) so a transient failure never loses a frame.
              const blob = await withRetry(() => extractFrame(vid, timecodeMs / 1000), 2, 400);
              const res = await withRetry(() => manuals.uploadCandidate(id, step.stepId, blob, timecodeMs), 3, 700);
              cands.push({
                timecodeMs: res.timecodeMs ?? timecodeMs,
                imageUrl: mediaUrl(res.imageUrl),
                mediaId: res.mediaId,
                label: step.candidates?.[i]?.label ?? SYNTH_LABELS[i],
                recommended: i === 0,
                bbox: toBBox(step),
              });
              // The decoded blob isn't kept — display is the R2 URL. GC reclaims `blob` here.
            } catch {
              // Skip this candidate; never abort the whole pass on one failure.
            }
            done += 1;
            if (!cancelled) setExtract((s) => ({ ...s, done: Math.min(done, total) }));
          }
          if (!cancelled && cands.length) {
            materialized += 1;
            setExtract((s) => ({
              ...s,
              candidatesByStep: { ...s.candidatesByStep, [step.stepId]: cands },
            }));
          }
        }
        if (!cancelled) {
          if (materialized > 0) {
            setExtract((s) => ({ ...s, status: "ready" }));
          } else {
            // Nothing materialized at all (every extract/upload failed) → honest error banner,
            // and one automatic full retry before leaving it to the 再試行 button.
            setExtract((s) => ({ ...s, status: "error", error: "フレームを抽出できませんでした" }));
            if (!autoRetriedRef.current) {
              autoRetriedRef.current = true;
              window.setTimeout(() => setExtractNonce((n) => n + 1), 1500);
            }
          }
        }
      } catch (e) {
        if (!cancelled) {
          setExtract((s) => ({ ...s, status: "error", error: (e as Error).message }));
          if (!autoRetriedRef.current) {
            autoRetriedRef.current = true;
            window.setTimeout(() => setExtractNonce((n) => n + 1), 1500);
          }
        }
      } finally {
        disposeVideo(video);
        if (objectUrl) URL.revokeObjectURL(objectUrl);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, isVideo, id, data?.media.originalVideoUrl, pendingKey, extractNonce]);

  // Final R2Candidate[] per step, in priority order:
  //   1) already-stored R2 candidates (from step.keyframeCandidates) — stable, no extraction.
  //   2) freshly materialized R2 candidates (extracted + uploaded this session).
  //   3) synthesized placeholder cells (demo / image flow / extraction unavailable).
  const candidatesByStep = useMemo(() => {
    return Object.fromEntries(
      steps.map((step) => {
        const stored = materializedByStep[step.stepId];
        if (stored?.length) return [step.stepId, stored] as const;
        const fresh = extract.candidatesByStep[step.stepId];
        if (fresh?.length) return [step.stepId, fresh] as const;
        const times = timesByStep[step.stepId] ?? [];
        return [step.stepId, synthCandidates(step, times)] as const;
      }),
    ) as Record<string, R2Candidate[]>;
  }, [steps, materializedByStep, timesByStep, extract.candidatesByStep]);

  function selIndex(stepId: string): number {
    return selections[stepId] ?? 0;
  }

  async function confirm() {
    setConfirming(true);
    const sel = steps.map((s) => {
      const cands = candidatesByStep[s.stepId] ?? [];
      const idx = selIndex(s.stepId);
      return { step: s, candidate: cands[idx] };
    });
    try {
      const withMedia = sel.filter((x) => x.candidate?.mediaId);
      if (withMedia.length) {
        // Adopt the chosen R2 candidate as the representative image (JSON keyframe), then run
        // AI bbox/label detection. Best-effort & serial; a failed detect must never block confirm.
        for (const x of withMedia) {
          await manuals.chooseKeyframe(id, x.step.stepId, {
            mediaId: x.candidate.mediaId!,
            timecodeMs: x.candidate.timecodeMs,
          });
          await manuals.detectStep(id, x.step.stepId).catch(() => {});
        }
      } else {
        // No materialized frames (demo / image fallback): fall back to time-only selection.
        await manuals.selectKeyframes(
          id,
          sel
            .filter((x) => x.candidate)
            .map((x) => ({ stepId: x.step.stepId, timecodeMs: x.candidate.timecodeMs })),
        );
      }
      // Persist the creation-time labels (store × category × tags) so the manual lands in the
      // right (store × category) book on the shelf. Best-effort — never blocks confirm.
      if (canLabel && labels) {
        await manuals
          .update(id, { storeId: labels.storeId, category: labels.category.trim() || null, tags: labels.tags })
          .catch(() => {});
      }
      // Picker is done with the cached recording.
      void removeSourceVideo(id);
    } catch {
      /* best-effort: proceed to preview regardless */
    } finally {
      setConfirming(false);
    }
    navigate(`/preview/${id}`, { state: { from: `/picker/${id}` } });
  }

  const extracting = isVideo && extract.status === "materializing";
  const extractFailed = isVideo && extract.status === "error";

  // 生成に不満なら行き止まりにしない: その場で破棄 or 撮影からやり直し(権限がある人のみ)。
  const manageRow = canManage ? (
    <div className="row" style={{ marginTop: 14, gap: 10, flexWrap: "wrap", alignItems: "center" }}>
      <Btn
        variant="ghost"
        icon={<Icon name="capture" size={15} />}
        onClick={() => void retakeCapture()}
        disabled={removing !== null || confirming}
      >
        {removing === "retake" ? "削除中…" : "撮影しなおす"}
      </Btn>
      <Btn
        variant="ghost"
        icon={<Icon name="close" size={15} />}
        onClick={() => void deleteGenerated()}
        disabled={removing !== null || confirming}
        style={{ borderColor: "color-mix(in srgb, var(--danger) 55%, transparent)", color: "color-mix(in srgb, var(--danger) 82%, white)" }}
      >
        {removing === "delete" ? "削除中…" : "削除する"}
      </Btn>
      {removeError ? (
        <span className="mono" role="alert" style={{ fontSize: 12, color: "var(--danger)" }}>
          {removeError}
        </span>
      ) : null}
    </div>
  ) : null;

  return (
    <div className="shell">
      <AppBar back="/dashboard" compactBrand>
        <Tag dot color="var(--info)">
          AI ANALYZED
        </Tag>
        <span className="statuspill">
          <span className="sdot sdot--ok" aria-hidden="true" />
          解析完了
        </span>
      </AppBar>

      <main className="wrap" style={{ paddingBottom: 120 }}>
        {loading ? (
          <LoadingState label="候補を読み込み中…" />
        ) : error ? (
          <ErrorState message={error} offline={offline} onRetry={reload} />
        ) : !steps.length ? (
          <section className="lead-block reveal" style={{ padding: "26px 0 8px" }}>
            <div className="kicker">STEP 02 / 04 — クリップ確認</div>
            <h1 className="display t-xl" style={{ marginTop: 10 }}>
              候補がまだ<span className="hl">ありません</span>。
            </h1>
            <p className="jp" style={{ color: "var(--muted)", maxWidth: 560, marginTop: 12 }}>
              この動画からはまだ手順が抽出されていません。処理が完了するまでお待ちください。
            </p>
            <div className="row" style={{ marginTop: 16 }}>
              <Btn onClick={reload}>再読み込み</Btn>
              <Btn variant="ghost" onClick={() => navigate(`/processing/${id}`)}>
                処理状況を見る
              </Btn>
            </div>
            {manageRow}
          </section>
        ) : (
          <>
            <section className="lead-block reveal" style={{ padding: "26px 0 8px" }}>
              <div className="kicker">STEP 02 / 04 — クリップ確認</div>
              <h1 className="display t-xl" style={{ marginTop: 10 }}>
                良い<span className="hl">瞬間</span>を、指1本で。
              </h1>
              <p className="jp" style={{ color: "var(--muted)", maxWidth: 560, marginTop: 12 }}>
                各手順についてAIが候補を3枚ずつ抽出しました。<b style={{ color: "var(--ink)" }}><Icon name="star" size={14} />がAIのおすすめ</b>
                。そのままでもOK、好みの1枚を選べます。
              </p>
              <div className="row" style={{ marginTop: 16, flexWrap: "wrap" }}>
                <Tag mono>{data?.title}</Tag>
                <Tag>
                  SOURCE · 動画{" "}
                  {mmss(
                    // duration_ms が無い過去データはクリップ範囲の最大値で代用
                    data?.media.videoDurationMs ||
                      steps.reduce((mx, s) => Math.max(mx, s.clipEndMs ?? s.videoTimecodeMs ?? 0), 0),
                  )}
                </Tag>
                <Tag dot color="var(--hi)">
                  {steps.length} STEPS
                </Tag>
              </div>
              {manageRow}
              {extracting ? (
                <div
                  className="row reveal"
                  role="status"
                  aria-live="polite"
                  style={{ marginTop: 14, alignItems: "center", gap: 12 }}
                >
                  <span
                    style={{
                      display: "inline-block",
                      width: 18,
                      height: 18,
                      borderRadius: "50%",
                      border: "2px solid var(--line)",
                      borderTopColor: "var(--hi)",
                      animation: "spin 1.1s linear infinite",
                    }}
                    aria-hidden="true"
                  />
                  <span className="mono" style={{ fontSize: 12, color: "var(--muted)" }}>
                    実フレームを抽出中… {extract.done}/{extract.total}
                  </span>
                </div>
              ) : null}
              {extractFailed ? (
                <Banner variant="warn" className="">
                  <span className="jp grow">
                    動画フレームの抽出に失敗しました{extract.error ? `（${extract.error}）` : ""}。
                    再試行するか、候補の時刻のままでも確定できます。
                  </span>
                  <Btn
                    style={{ whiteSpace: "nowrap" }}
                    onClick={() => {
                      autoRetriedRef.current = false; // each manual retry earns one more auto follow-up
                      setExtractNonce((n) => n + 1);
                    }}
                  >
                    再試行
                  </Btn>
                </Banner>
              ) : null}

              {canLabel && labels ? (
                <Card crop pad className="reveal d1" style={{ marginTop: 18 }}>
                  <div className="kicker" style={{ marginBottom: 4 }}>ラベル — 検索・整理用</div>
                  <p className="jp" style={{ color: "var(--muted)", fontSize: 13, margin: "0 0 14px" }}>
                    カテゴリ・タグは検索と整理に使われます。本棚への配置は、この後の<b>提出時に「本」を選んで</b>行います。
                  </p>
                  <LabelingBlock value={labels} onChange={setLabels} stores={storeList} categorySuggestions={catSuggest} />
                </Card>
              ) : null}
            </section>

            {steps.map((step, i) => {
              const cands = candidatesByStep[step.stepId];
              const sel = selIndex(step.stepId);
              return (
                <Card key={step.stepId} crop className={`stepcard reveal${i ? ` d${i}` : ""}`}>
                  <div className="idx">
                    {String(step.order).padStart(2, "0")}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div className="spread" style={{ alignItems: "flex-start" }}>
                      <h2 className="t-lg jp">{step.title}</h2>
                      {step.clipStartMs != null && step.clipEndMs != null ? (
                        <Tag mono>
                          {mmss(step.clipStartMs)}–{mmss(step.clipEndMs)}
                        </Tag>
                      ) : null}
                    </div>
                    <p className="hint" style={{ margin: "6px 0 14px", fontSize: 12, color: "var(--muted)", fontFamily: "var(--font-mono)" }}>
                      <span style={{ color: "var(--hi)" }}><Icon name="more" size={14} /></span> 候補をタップして選択 — 手がブレず対象が明瞭な瞬間を
                    </p>
                    <CandidatePicker
                      candidates={cands}
                      selected={sel}
                      onSelect={(idx) => setSelections((s) => ({ ...s, [step.stepId]: idx }))}
                      ariaLabel={`手順${step.order}の代表フレーム候補`}
                    />
                    <p className="jp" style={{ margin: "16px 0 0", fontSize: 15 }}>
                      {step.caption}
                    </p>
                    {step.cautions.map((c, ci) => (
                      <Banner key={ci} variant={cautionVariant(c.level)} className="" >
                        <span className="jp">{c.text}</span>
                      </Banner>
                    ))}
                  </div>
                </Card>
              );
            })}
          </>
        )}
      </main>

      <div className="actionbar">
        <div className="grow">
          <div className="mono" style={{ fontSize: 12, color: "var(--muted)" }}>
            {steps.length} 手順 · {extracting ? "フレーム抽出中…" : "すべて選択済み"}
          </div>
        </div>
        <Btn variant="ghost" lg onClick={() => navigate(`/timeline/${id}`)}>
          タイムラインで微調整
        </Btn>
        <Btn
          variant="hi"
          lg
          icon={<Icon name="target" />}
          onClick={confirm}
          disabled={loading || !steps.length || extracting || confirming}
        >
          {confirming ? "送信中…" : "この内容で確定"}
        </Btn>
      </div>
    </div>
  );
}

